import { z } from "zod";
import {
  AiActionBidBoundsV1Schema,
  AiActionExclusionsV1Schema,
  AiActionFlagsV1Schema,
  AiActionPolicyV1Schema,
} from "./ai-action-policy";
import { ApprovalRiskThresholdV1Schema } from "./ai-policy";

/** Existing base-bid scopes remain valid; full operation consent is explicit and independently versioned. */
export const CampaignAiActionScopeV1Schema = z
  .object({
    version: z.literal(1),
    source: z.enum(["campaign", "smart_creation"]),
    baseBid: z.boolean(),
    bidBounds: AiActionBidBoundsV1Schema.optional(),
    revision: z.number().int().positive().optional(),
    actions: AiActionFlagsV1Schema.optional(),
    budgetBoostPercentLimit: z.number().finite().positive().max(100).optional(),
    budgetBoostExtraSpendConsent: z.boolean().optional(),
    budgetBoostDurationDays: z.number().int().min(1).max(7).optional(),
    harvestExclusions: AiActionExclusionsV1Schema.optional(),
    negativeExclusions: AiActionExclusionsV1Schema.optional(),
    approvalRiskThreshold: ApprovalRiskThresholdV1Schema.optional(),
    dryRun: z.boolean().optional(),
  })
  .strict()
  .superRefine((scope, context) => {
    if (!scope.actions) return;
    if (scope.baseBid !== scope.actions.baseBid) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["baseBid"],
        message: "baseBid must agree with the complete action flags",
      });
    }
    for (const key of ["revision", "approvalRiskThreshold", "dryRun"] as const) {
      if (scope[key] === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: "Operation scopes require explicit policy settings",
        });
      }
    }
    const result = AiActionPolicyV1Schema.safeParse({
      version: scope.version,
      revision: scope.revision,
      source: "campaign",
      shopId: "00000000-0000-4000-8000-000000000001",
      campaignId: "00000000-0000-4000-8000-000000000002",
      actions: scope.actions,
      bidBounds: scope.bidBounds,
      budgetBoostPercentLimit: scope.budgetBoostPercentLimit,
      budgetBoostExtraSpendConsent: scope.budgetBoostExtraSpendConsent,
      budgetBoostDurationDays: scope.budgetBoostDurationDays,
      harvestExclusions: scope.harvestExclusions,
      negativeExclusions: scope.negativeExclusions,
      approvalRiskThreshold: scope.approvalRiskThreshold,
      dryRun: scope.dryRun,
    });
    if (!result.success) {
      for (const issue of result.error.issues) {
        context.addIssue(issue);
      }
    }
  });
export type CampaignAiActionScopeV1 = z.infer<typeof CampaignAiActionScopeV1Schema>;

export const CampaignAiActionScopePatchV1Schema = z
  .object({ version: z.literal(1), baseBid: z.boolean() })
  .strict();
export type CampaignAiActionScopePatchV1 = z.infer<typeof CampaignAiActionScopePatchV1Schema>;

/** The source is frozen with each proposal so a later setting edit cannot rewrite its audit. */
export const AiActionScopeSnapshotV1Schema = z
  .object({
    version: z.literal(1),
    source: z.enum(["legacy_unscoped", "campaign", "smart_creation"]),
    baseBid: z.boolean(),
    bidBounds: AiActionBidBoundsV1Schema.optional(),
  })
  .strict();
export type AiActionScopeSnapshotV1 = z.infer<typeof AiActionScopeSnapshotV1Schema>;
